import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { Image, Menu as SemanticMenu, Responsive, Icon } from 'semantic-ui-react';
import styled from 'styled-components';

import packageInfo from '../../package.json';
import { menuOptions } from '../services/menu';
import { MenuItemProps } from './types';

import MenuItem from './MenuItem';

import logo from '../resources/imgs/logo-lado-bom-seguros.svg';
import partialLogo from '../resources/imgs/partial-logo-02.svg';

import { iRootState, iRootDispatch } from '../store';
import AuthorizationContainer from '../components/AuthorizationContainer';
import { ActionEnum } from '../enums/authz-action.enum';
import useWindowSize from '../hooks/useWindowSize';
import { ConfigKeysEnum, useConfigKey } from '../services/configKeys';

import { get } from '../services/storage';
import { UserRolesEnum } from '../enums/user-roles.enum';

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 1em;
`;

const Logo = styled(Image)`
  width: 13em;

  @media (max-width: 770px) {
    width: 8em;
  }
`;

const MenuCloseIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translate(-50%, -50%);
  color: ${props => props.theme.main.color};
`;

const SidebarGrid = styled(Responsive) <{ isCollapsed?: boolean }>`
  background-color: #fff;
  width: ${props => props.isCollapsed ? '70px' : '268px'} !important;
  min-width: ${props => props.isCollapsed ? '70px' : '268px'} !important;
  max-width: ${props => props.isCollapsed ? '70px' : '268px'} !important;
  padding: 0 0 100px;
  border-right: 1px solid #e6e6e6;
  overflow-x: hidden;
  overflow-y: auto;
  box-shadow: none;
  height: 100vh;
  transition: all 0.3s ease;
`;

const VersionText = styled.div`
  position: absolute;
  bottom: 0.5rem;
  width: 100%;

  p {
    text-align: center;
    font-size: 12px;
  }
`;

const AppMenuItemsWrapper = styled.div`
  background-color: #fff;
  border-radius: none;
  font-size: 1em;
  display: flex;
  flex-direction: column;

  .ui.vertical.menu > .active.item:only-child {
    border-radius: none;
  }
`;

const AppMenu = styled(SemanticMenu)`
  background-color: #e1e8ed !important;
`;

const LogoContainerWithCollapse = styled.div<{ isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${props => props.isCollapsed ? '0.5em' : '1em'};
  transition: padding 0.3s ease;
  min-height: 80px;
  
  .logo {
    transition: all 0.3s ease;
    ${props => props.isCollapsed && `
      width: 40px; 
      height: 40px;
    `}
  }
`;

const mapState = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  sidebarCollapse: state.sidebarCollapse
});

const mapDispatch = (dispatch: iRootDispatch) => ({});

export type Props = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> &
{ handleToggleSidebar: () => void };

const Menu = ({
  user,
  availableBrokersAndEstates,
  sidebarCollapse,
  handleToggleSidebar
}: Props) => {
  const [options, setOptions] = useState<MenuItemProps[]>([]);
  const [canView, setCanView] = useState<boolean>(false);

  const { isMobile } = useWindowSize();

  const { data: configData, isSuccess: configIsSuccess } = useConfigKey(
    ConfigKeysEnum.ACCESS_OPERATIONAL_EMAILS
  );

  useEffect(() => {
    if (configIsSuccess && configData) {
      if (user.email && configData.value.includes(user.email)) {
        setCanView(true);
      }
    }
  }, [configIsSuccess, configData, setCanView, user.email]);

  useEffect(() => {
    let menuOpts = menuOptions;

    if (
      user.role === UserRolesEnum.franchiseAdmin ||
      user.role === UserRolesEnum.multiFranchiseAdmin
    ) {
      menuOpts = menuOpts.filter(menu => menu.alias !== 'MANAGEMENT');
    }

    if (user.role?.includes('ESTATE')) {
      menuOpts.forEach(submenu => {
        if (submenu.name === 'insurance') {
          submenu.submenuItems = submenu.submenuItems?.filter(
            submenu => submenu.name !== 'reports-form'
          );
        }

        if (user.role === 'ESTATE_USER' && submenu.name === 'user') {
          submenu.submenuItems = submenu.submenuItems?.filter(
            submenu => submenu.name !== 'new-user'
          );
        }
      });
    }

    if (!availableBrokersAndEstates.enableFireInsuranceMenu) {
      menuOpts = menuOpts.filter(menu => menu.alias !== 'FIRE');
    }

    // Filtrar submenu items baseado em role
    menuOpts.forEach(menu => {
      if (menu.submenuItems) {
        menu.submenuItems = menu.submenuItems.filter(item => {
          if ((item as any).requiresRole) {
            return user.role === (item as any).requiresRole;
          }
          return true;
        });
      }
    });

    setOptions(menuOpts);
  }, [availableBrokersAndEstates.enableFireInsuranceMenu, user.role]);

  const userRole = get('role');

  return (
    <SidebarGrid isCollapsed={sidebarCollapse.isCollapsed}>
      <LogoContainerWithCollapse isCollapsed={sidebarCollapse.isCollapsed}>
        <Logo className="logo" src={sidebarCollapse.isCollapsed ? partialLogo : logo} />

        {isMobile && (
          <MenuCloseIcon name="arrow left" size="large" onClick={handleToggleSidebar} />
        )}
      </LogoContainerWithCollapse>

      <AppMenu fluid vertical inverted borderless>
        <AppMenuItemsWrapper>
          {options.map((item: MenuItemProps) => {
            if (['MANAGEMENT'].includes(item.name)) {
              if (
                userRole === UserRolesEnum.estateAdmin ||
                userRole === UserRolesEnum.estateUser
              ) {
                item.submenuItems = item.submenuItems?.filter(
                  sub => sub.name === 'REPORTS'
                );
              }
              return <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />;
            }

            if (
              (['FRANCHISES'].includes(item.name) &&
                userRole === UserRolesEnum.multiFranchiseAdmin) ||
              userRole === UserRolesEnum.franchiseAdmin
            ) {
              const sub = item.submenuItems?.filter(sub => {
                if (userRole === UserRolesEnum.franchiseAdmin) {
                  return (
                    sub.name !== 'new-franchise' &&
                    sub.name !== 'list-master-franchises' &&
                    sub.name !== 'list-franchises'
                  );
                }
                return (
                  sub.name !== 'new-franchise' && sub.name !== 'list-master-franchises'
                );
              });
              item.submenuItems = sub;
              return <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />;
            }

            if (item.name !== 'OPERATIONAL') {
              return (
                <AuthorizationContainer
                  key={item.id}
                  action={ActionEnum.read}
                  feature={item.name}
                >
                  <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} />
                </AuthorizationContainer>
              );
            }

            return canView ? <MenuItem key={item.id} {...item} isCollapsed={sidebarCollapse.isCollapsed} /> : <Fragment />;
          })}
        </AppMenuItemsWrapper>

        {!sidebarCollapse.isCollapsed && (
          <VersionText>
            <p>v{packageInfo.version}</p>
          </VersionText>
        )}
      </AppMenu>
    </SidebarGrid>
  );
};

export default connect(mapState, mapDispatch)(Menu);
